import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { scrollToId } from "./SmoothScroll";

const LINES = [
  [{ t: "DIVERSÃO QUE" }],
  [{ t: "CONQUISTA" }],
  [{ t: "TODAS AS", c: "text-gold-grad" }],
  [{ t: "IDADES.", c: "text-red-grad" }],
];
const EASE = [0.16, 1, 0.3, 1];
const FACTS = ["4h de locação", "Jogadas ilimitadas", "Monitor incluso", "Translado + montagem"];

const MaskedTitle = () => (
  <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.95] tracking-[-0.02em] text-white"
    style={{ fontSize: "clamp(2rem, 6vw, 5.6rem)" }}>
    {LINES.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em]">
        <motion.span className={`block ${line[0].c || ""}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
          transition={{ duration: 1.1, delay: 0.35 + i * 0.12, ease: EASE }}>
          {line[0].t}
        </motion.span>
      </span>
    ))}
  </h1>
);

const ClawStage = ({ rx, ry, yFrame }) => (
  <motion.div style={{ y: yFrame }} className="relative h-[440px] sm:h-[520px] lg:h-[640px] [perspective:1200px] overflow-hidden lg:overflow-visible">
    <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative h-full [transform-style:preserve-3d]">
      <motion.div initial={{ clipPath: "inset(100% 0 0 0 round 220px 220px 28px 28px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 220px 220px 28px 28px)" }}
        transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
        className="absolute inset-x-0 bottom-0 top-[18%] overflow-hidden border border-[#FFD200]/30 bg-[#111219]">
        <img src="/media/hero.jpg" alt="Garra da máquina de pelúcias pegando um prêmio" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,210,0,0.35),transparent_60%)] mix-blend-screen" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080C] via-transparent to-transparent" />
        <div className="absolute inset-x-6 top-6 h-4 bulbs opacity-70" />
      </motion.div>
      <div className="absolute inset-x-0 top-0 flex justify-center pointer-events-none" style={{ transform: "translateZ(60px)" }}>
      <motion.div initial={{ y: -520 }} animate={{ y: 0 }} transition={{ type: "spring", stiffness: 40, damping: 9, delay: 1.1 }}
        className="w-[150px] sm:w-[190px] lg:w-[220px]">
        <motion.div animate={{ rotate: [-3, 3, -3] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "50% 0%" }}>
          <div className="mx-auto w-[3px] h-[600px] -mt-[600px] bg-gradient-to-b from-transparent via-zinc-400 to-zinc-200" />
          <img src="/brand/claw.png?v=2" alt="" className="w-full drop-shadow-[0_20px_40px_rgba(255,210,0,0.35)]" />
        </motion.div>
      </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-6 left-6 right-6 flex items-end justify-between [transform:translateZ(40px)]">
        <div className="rounded-2xl bg-black/60 backdrop-blur-md border border-white/10 px-4 py-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">Por evento</p>
          <p className="font-display text-xl font-bold text-white">4h · ∞ jogadas</p>
        </div>
      </motion.div>
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFrame = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-screen pt-[110px] lg:pt-[120px] pb-16 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_75%_10%,rgba(255,210,0,0.10),transparent),radial-gradient(ellipse_50%_40%_at_10%_90%,rgba(232,32,42,0.12),transparent)]" />
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono text-xs uppercase tracking-[0.28em] text-[#FFD200] mb-8 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FFD200]" /> Aluguel de máquina de pelúcias para eventos
          </motion.p>
          <MaskedTitle />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed">
            A garra desce, a festa para. Levamos a máquina de pelúcias até o seu evento, montamos tudo e um monitor
            cuida da diversão por 4 horas de jogadas ilimitadas.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-3">
            <Link to="/reservar" data-testid="hero-cta-booking"
              className="group inline-flex items-center gap-3 rounded-full bg-[#FFD200] text-[#08080C] font-semibold pl-7 pr-2 py-2 hover:bg-white transition-colors">
              Reservar minha data
              <span className="w-10 h-10 rounded-full bg-[#08080C] text-[#FFD200] grid place-items-center transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight className="w-5 h-5" />
              </span>
            </Link>
            <button data-testid="hero-cta-packages" onClick={() => scrollToId("pacotes")}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white px-7 py-4 hover:border-[#FFD200] hover:text-[#FFD200] transition-colors">
              Ver pacotes <ArrowDownRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5"><ClawStage rx={rx} ry={ry} yFrame={yFrame} /></div>
      </div>
      <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="relative max-w-[1400px] mx-auto px-5 lg:px-10 mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
        {FACTS.map((f, i) => (
          <li key={f} className="py-5 pr-4 font-mono text-xs uppercase tracking-[0.18em] text-zinc-300 flex items-center gap-3">
            <span className="text-[#FFD200]">0{i + 1}</span>{f}
          </li>
        ))}
      </motion.ul>
    </section>
  );
};
